import { useState, useEffect } from "react";
import { fetchAuditLogsApi } from "../Lolbin/Api/lolbinapi";
import { useParams } from "react-router-dom";
import { Card, CardContent } from "../../ui/card";
import { Skeleton } from "../../ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import {
  Settings,
  ArrowLeft,
  CheckCircle,
  Key,
  BarChart3,
  Activity,
  Bell,
  Globe,
  TrendingUp,
  ChevronRight
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import AuditLogsDetails from "../AuditLogs/AuditLogsDetails";
import AlertDetails from "../Alerts/AlertsDetails";
import { myLocalStorage } from "../../StorageHelper";
import useUserStore from "../../../services/userStore";
import OverviewCardHeader from "../Overview/OverviewCardHeader";
import OverviewEmptyState from "../Overview/OverviewEmptyState";
import "../Overview/EndpointOverview.css";
import "../CloudAnalytics/CloudEntityAnalytics.css";
import "../Endpoints/DeviceDetails/DeviceDetailPage.css";
import "./ActiveDirectory.css";

interface IPAddressUsage {
  ipAddress: string;
  accessCount: number;
  lastSeen: string;
}


interface SPNOperation {
  spn: string;
  accessCount: number;
  lastUsed: string;
}


const isInternalIP = (ip: string): boolean => {
  const parts = ip.split('.').map(Number);
  if (parts.length !== 4) return false;
  if (parts[0] === 10) return true;
  if (parts[0] === 172 && parts[1] >= 16 && parts[1] <= 31) return true;
  if (parts[0] === 192 && parts[1] === 168) return true;
  if (parts[0] === 127) return true;
  return false;
};


export default function ADServiceAccountAnalytics() {
  const [lastActivity, setLastActivity] = useState<string>("-");
  const params = useParams();
  const [activeTab, setActiveTab] = useState("alerts");
  const [controlsContainer, setControlsContainer] = useState<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const computerEmail = params.name || '';
  console.log('Service Account Name:', computerEmail);


  // State for dynamic analytics
  const [activityTimeline, setActivityTimeline] = useState<{ time: string; events: number }[]>([]);
  const [topIPAddresses, setTopIPAddresses] = useState<IPAddressUsage[]>([]);
  const [spnOperations, setSpnOperations] = useState<SPNOperation[]>([]);
  const [loading, setLoading] = useState(false);
  const tenantName = myLocalStorage.getItem("latestTenant")?.tenantName || "";
  const { email } = useUserStore((state) => state.user);



  const fetchData = async () => {
    let latestTimestamp = "";
    // Helper to normalize ::ffff:IPv4 to IPv4
    const normalizeIP = (ip: string) => {
      const match = ip.match(/^::ffff:(\d+\.\d+\.\d+\.\d+)$/);
      return match ? match[1] : ip;
    };
    setLoading(true);
    try {
      const filterPagination = 'lm';
      const computerName = '';

      const response = await fetchAuditLogsApi(
        "ondevice-auditlog",
        tenantName,
        filterPagination,
        computerName,
        "",
        computerEmail,
        "",
        email,
        true,
        "",
        "",
        1,
        5000,

      );
      if (response) {

        let logs = response || [];
        const timelineMap = new Map<string, number>();
        const ipMap = new Map<string, { count: number; lastSeen: string }>();
        const spnMap = new Map<string, { count: number; lastUsed: string }>();

        logs.forEach((log: any) => {
          // Track latest activity timestamp
          if (log._time && (!latestTimestamp || log._time > latestTimestamp)) {
            latestTimestamp = log._time;
          }
          // After processing logs, set last activity
          if (latestTimestamp) {
            const d = new Date(latestTimestamp);
            setLastActivity(isNaN(d.getTime()) ? latestTimestamp : d.toLocaleString());
          } else {
            setLastActivity("-");
          }
          let event: Record<string, any> = {};
          try {
            event = log?.eventJson ? JSON.parse(log.eventJson) as Record<string, any> : {};
          } catch (e) { event = {}; }

          if (log._time) {
            const date = new Date(log._time);
            const day = date.toISOString().split('T')[0];
            timelineMap.set(day, (timelineMap.get(day) || 0) + 1);
          }

          let srcIP = '';
          if (event.IpAddress && typeof event.IpAddress === 'string') {
            srcIP = event.IpAddress;
          } else if (event.SourceIp && typeof event.SourceIp === 'string') {
            srcIP = event.SourceIp;
          }
          srcIP = srcIP ? normalizeIP(srcIP) : srcIP;

          const prev = ipMap.get(srcIP);
          if (!prev || (log._time && log._time > prev.lastSeen)) {
            ipMap.set(srcIP, { count: (prev ? prev.count : 0) + 1, lastSeen: log._time || '' });
          } else {
            ipMap.set(srcIP, { count: (prev ? prev.count : 0) + 1, lastSeen: prev.lastSeen });
          }
          let svc = '';
          if (typeof event['ServiceName'] === 'string') {
            svc = event['ServiceName'];
          } else if (typeof event['Service'] === 'string') {
            svc = event['Service'];
          } else if (typeof event['ServicePrincipalName'] === 'string') {
            svc = event['ServicePrincipalName'];
          }
          if (svc) {
            const prev = spnMap.get(svc);
            if (!prev || (log._time && log._time > prev.lastUsed)) {
              spnMap.set(svc, { count: (prev ? prev.count : 0) + 1, lastUsed: log._time || '' });
            } else {
              spnMap.set(svc, { count: (prev ? prev.count : 0) + 1, lastUsed: prev.lastUsed });
            }
          }
        });

        // Prepare timeline (last 7 days)
        const now = new Date();
        const timelineArr: { time: string; events: number }[] = [];
        for (let i = 6; i >= 0; i--) {
          const d = new Date(now);
          d.setDate(now.getDate() - i);
          const key = d.toISOString().split('T')[0];
          timelineArr.push({ time: `${i === 0 ? 'Today' : `${i}d ago`}`, events: timelineMap.get(key) || 0 });
        }
        setActivityTimeline(timelineArr);

        // Prepare top IPs, filter out empty
        setTopIPAddresses(
          Array.from(ipMap.entries())
            .filter(([ip]) => ip && ip.trim() !== "")
            .map(([ip, v]) => ({ ipAddress: ip, accessCount: v.count, lastSeen: v.lastSeen }))
            .sort((a, b) => b.accessCount - a.accessCount)
            .slice(0, 10)
        );

        // Prepare top SPNs
        const spnArr = Array.from(spnMap.entries()).map(([spn, v]) => ({ spn, accessCount: v.count, lastUsed: v.lastUsed }));
        spnArr.sort((a, b) => b.accessCount - a.accessCount);
        setSpnOperations(spnArr.slice(0, 10));

      }


      // If no relevant fields found, optionally show a message (handled in render)
    } catch (e) {
      setActivityTimeline([]);
      setTopIPAddresses([]);
      setSpnOperations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computerEmail, activeTab]);


  const detailTabs = [
    { id: "alerts", label: "Alerts", icon: Bell },
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "activity", label: "Activity", icon: Activity },
  ];

  const renderUsageList = (
    items: { key: string; label: string; count: number; external?: boolean }[],
    emptyIcon: typeof Key,
    emptyMessage: string
  ) => {
    if (items.length === 0) {
      return <OverviewEmptyState icon={emptyIcon} message={emptyMessage} />;
    }
    const total = items.reduce((sum, item) => sum + item.count, 0);
    const maxCount = items[0]?.count || 1;
    return (
      <div className="overview-ranking-list">
        {items.map((item, idx) => {
          const pct = (item.count / total) * 100;
          const barWidth = (item.count / maxCount) * 100;
          return (
            <div key={item.key} className="overview-ranking-row flex items-center justify-between">
              <div className="overview-ranking-identity flex flex-1 min-w-0">
                <span className="overview-rank flex-shrink-0">{idx + 1}</span>
                <div className="min-w-0">
                  <span className="overview-ranking-name ad-usage-name">
                    {item.label}
                    {item.external && <span className="ad-usage-flag">External</span>}
                  </span>
                  <span className="overview-ranking-count">{item.count.toLocaleString()} events</span>
                </div>
              </div>
              <div className="overview-ranking-stats flex items-center">
                <div className="overview-ranking-track flex-1 overflow-hidden rounded-full bg-muted">
                  <div className="overview-ranking-fill h-full rounded-full" style={{ width: `${barWidth}%` }} />
                </div>
                <span className="overview-ranking-percent text-right text-muted-foreground">{pct.toFixed(1)}%</span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="endpoint-detail-page ad-detail-page custom-scrollbar flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden overflow-y-auto bg-background">
      <div className="cea-header endpoint-detail-header">
        <div className="cea-entity-row endpoint-detail-entity-row">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="cea-back-icon-btn focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            aria-label="Back to AD Management"
            title="Back to AD Management"
            data-testid="button-back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>

          <div className="cea-avatar">
            <Settings />
          </div>
          <div className="cea-entity-info endpoint-detail-entity-info">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <h1 className="cea-entity-name min-w-0" title={computerEmail} data-testid="text-entity-name">
                {computerEmail}
              </h1>
              <span className="ad-detail-status">
                <CheckCircle aria-hidden="true" />
                Active
              </span>
            </div>
            <div className="cea-entity-sub">
              <span>Last activity <span className="ad-detail-strong">{lastActivity}</span></span>
            </div>
            <nav aria-label="Breadcrumb" className="mt-3 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
              <span>Active Directory</span>
              <ChevronRight className="w-3.5 h-3.5 shrink-0" />
              <span>Service Accounts</span>
              <ChevronRight className="w-3.5 h-3.5 shrink-0" />
              <span className="font-medium text-foreground break-all" aria-current="page">{computerEmail}</span>
            </nav>
          </div>
          <div className="endpoint-detail-actions ad-detail-stats">
            <div className="ad-detail-stat">
              <span className="ad-detail-stat-icon" aria-hidden="true"><Globe /></span>
              <div>
                <span className="ad-detail-stat-value">{topIPAddresses.length}</span>
                <span className="ad-detail-stat-label">IP Addresses</span>
              </div>
            </div>
            <div className="ad-detail-stat" data-tone="violet">
              <span className="ad-detail-stat-icon" aria-hidden="true"><Key /></span>
              <div>
                <span className="ad-detail-stat-value">{spnOperations.length}</span>
                <span className="ad-detail-stat-label">SPNs</span>
              </div>
            </div>
            <div ref={setControlsContainer} className="endpoint-detail-controls ad-detail-controls" />
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex min-w-0 flex-none flex-col">
        <div className="endpoint-detail-tabs-wrapper">
          <TabsList unstyled aria-label="Service account details" className="cea-tabs endpoint-detail-tabs custom-scrollbar">
            {detailTabs.map((tab) => (
              <TabsTrigger
                unstyled
                key={tab.id}
                value={tab.id}
                className={`cea-tab ${activeTab === tab.id ? "active" : ""}`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="shrink-0" />
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <TabsContent value="overview" className="mt-0">
          <div className="endpoint-overview">
            <Card className="overview-card overview-timeline-card">
              <OverviewCardHeader
                fromdetails
                title="Activity Timeline"
                description="Event frequency over last 7 days"
                icon={TrendingUp}
              />
              <CardContent className="overview-card-content">
                {loading ? (
                  <Skeleton className="h-[270px] w-full rounded-xl" />
                ) : (
                  <div className="overview-timeline">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={activityTimeline} margin={{ top: 12, right: 12, left: -20, bottom: 0 }}>
                        <defs>
                          <linearGradient id="colorEvents" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.22} />
                            <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.02} />
                          </linearGradient>
                        </defs>
                        <XAxis
                          dataKey="time"
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                          tickMargin={12}
                          interval="preserveStartEnd"
                        />
                        <YAxis
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                        />
                        <Tooltip wrapperClassName="overview-chart-tooltip" />
                        <Area
                          type="monotone"
                          dataKey="events"
                          stroke="hsl(var(--primary))"
                          strokeWidth={2.5}
                          fill="url(#colorEvents)"
                          activeDot={{ r: 4, fill: "hsl(var(--primary))", stroke: "hsl(var(--card))", strokeWidth: 2 }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <Card className="overview-card">
                <OverviewCardHeader
                  fromdetails
                  title="Top SPNs"
                  description="Most used Service Principal Names"
                  icon={Key}
                  detail={!loading && spnOperations.length > 0 ? `${spnOperations.length} SPNs` : undefined}
                />
                <CardContent className="overview-card-content">
                  {loading ? (
                    <div className="space-y-3">
                      <Skeleton className="h-12 w-full rounded-xl" />
                      <Skeleton className="h-12 w-full rounded-xl" />
                    </div>
                  ) : renderUsageList(
                    spnOperations.map((spn) => ({ key: spn.spn, label: spn.spn, count: spn.accessCount })),
                    Key,
                    "No service principal names recorded."
                  )}
                </CardContent>
              </Card>

              <Card className="overview-card">
                <OverviewCardHeader
                  fromdetails
                  title="Top IP Addresses"
                  description="Most frequent source IPs"
                  icon={Globe}
                  detail={!loading && topIPAddresses.length > 0 ? `${topIPAddresses.length} IPs` : undefined}
                />
                <CardContent className="overview-card-content">
                  {loading ? (
                    <div className="space-y-3">
                      <Skeleton className="h-12 w-full rounded-xl" />
                      <Skeleton className="h-12 w-full rounded-xl" />
                    </div>
                  ) : renderUsageList(
                    topIPAddresses.map((ip) => ({
                      key: ip.ipAddress,
                      label: ip.ipAddress,
                      count: ip.accessCount,
                      external: !isInternalIP(ip.ipAddress),
                    })),
                    Globe,
                    "No source IP addresses recorded."
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="activity" className="mt-0">
          <AuditLogsDetails fromdetails={true} computerName="" computeremail={computerEmail} controlsContainer={controlsContainer} />
        </TabsContent>

        <TabsContent value="alerts" className="mt-0">
          <AlertDetails fromdetails={true} computerName="" computeremail={computerEmail} controlsContainer={controlsContainer} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
